import { ReactElement, useState } from 'react'
import { round } from 'lodash-es'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Tab, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import type { ShearAnchorLoadsDTO } from '@api/fastAPI.schemas'
import useLoadsCombinedStore from '@editor/pages/Results2/stores/useLoadsCombinedStore'
import LoadResultsGrid from '../../LoadResultsTable'
import {
  categoryColumn,
  combineRowsBy,
  mergeOrigins,
  numberColumn,
  sourceColumn,
  textColumn,
} from '../../LoadResultsTable/columns'
import useLoadVisibilityColumn from '../../LoadResultsTable/useLoadVisibilityColumn'
import { formatLoadCase, formatLoadCategoryShort } from '../../Results2AnchorDetailPane/utils'
import { formatNumber, shearAnchorLoadGuid } from '../utils'

type ShearAnchorLoadTab = 'horizontal' | 'point' | 'line'

interface Props {
  loads?: ShearAnchorLoadsDTO
  elementGuid: string
  /** The project's sliding friction coefficient the friction column was derived with. */
  frictionCoefficient?: number
}

/** The vertical loads are the ones standing on the element, so positions are wall-relative. */
/**
 * Last column of both vertical tables: the load's share of the sliding friction as a shear flow,
 * i.e. already divided by the element length the check divides by. Shown as a flow rather than a
 * force so the column can be summed and multiplied by the combination coefficient to arrive at
 * s_Reibung,Rd by hand.
 */
const frictionColumn = numberColumn(
  'friction',
  <span>
    s<sub>Reib</sub>
  </span>,
  'sReib',
  'kN/m',
)

const verticalLoadDescription = (frictionCoefficient: number | undefined): string =>
  'Die Positionsangaben beziehen sich auf die Wand. Aufgeführt sind die Lasten, aus denen die ' +
  'Gleitreibung ermittelt wird — Wind und Erdbeben bleiben dabei unberücksichtigt. ' +
  's_Reib ist der charakteristische Reibungsanteil der jeweiligen Last, bezogen auf die ' +
  `Elementlänge (Reibungskoeffizient ${formatNumber(frictionCoefficient)}, Projekteinstellung).`

/**
 * Einwirkungen of one shear anchor, in the same layout as the tensile anchor's tables: the
 * horizontal loads driving the acting shear flow, and the vertical point/line loads the sliding
 * friction reducing it is derived from.
 *
 * There is no anchor-force column here: a shear anchor is not a point support, so no load has a
 * share "on the anchor" — it is the running shear flow of the whole element that is checked.
 */
const ShearAnchorLoadResults = ({
  loads,
  elementGuid,
  frictionCoefficient,
}: Props): ReactElement => {
  const [tab, setTab] = useState<ShearAnchorLoadTab>('horizontal')
  const loadsCombined = useLoadsCombinedStore(state => state.loadsCombined)
  const visibilityColumn = useLoadVisibilityColumn()

  if (!loads) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        Keine Einwirkungen verfügbar
      </Typography>
    )
  }

  const horizontalColumns: GridColDef[] = [
    textColumn('loadCase', 'Lastfall', 1, 80),
    categoryColumn,
    numberColumn(
      'F_hk',
      <span>
        F<sub>h,k</sub>
      </span>,
      'Fh,k',
      'kN',
      110,
    ),
    // The load's share of the acting shear flow, over the element length. It is
    // the magnitude of F_h,k: the check takes every horizontal load's absolute
    // value, so a load pushing the other way still adds to the flow.
    numberColumn(
      's_h',
      <span>
        s<sub>h</sub>
      </span>,
      'sh',
      'kN/m',
    ),
  ]
  const horizontalRows = loads.horizontal_loads.map((load, index) => ({
    id: index,
    loadGuids: [shearAnchorLoadGuid(load.load_guid, elementGuid, 'horizontal', index)],
    loadCase: formatLoadCase(load.load_case, load.load_case_type),
    category: formatLoadCategoryShort(load.load_case),
    F_hk: load.f_x / 1000,
    s_h: load.shear_flow / 1000,
  }))
  const horizontalRowsDisplayed = loadsCombined
    ? combineRowsBy(
        horizontalRows,
        row => row.loadCase,
        (a, b) => ({
          ...a,
          loadGuids: [...a.loadGuids, ...b.loadGuids],
          F_hk: a.F_hk + b.F_hk,
          s_h: a.s_h + b.s_h,
        }),
      )
    : horizontalRows

  const pointColumns: GridColDef[] = [
    sourceColumn,
    textColumn('loadCase', 'Lastfall'),
    categoryColumn,
    numberColumn('pos', 'Position', 'Position', 'm'),
    numberColumn(
      'force',
      <span>
        F<sub>z</sub>
      </span>,
      'Fz',
      'kN',
    ),
    frictionColumn,
  ]
  const pointRows = loads.vertical_point_loads.map((load, index) => ({
    id: index,
    loadGuids: [shearAnchorLoadGuid(load.load_guid, elementGuid, 'vertical-point', index)],
    origin: load.origin_label && load.origin_label !== '-' ? load.origin_label : 'extern',
    loadCase: formatLoadCase(load.load_case, load.load_case_type),
    category: formatLoadCategoryShort(load.load_case),
    pos: load.pos,
    force: load.f_z / 1000,
    friction: load.friction_shear_flow / 1000,
  }))
  const pointRowsDisplayed = loadsCombined
    ? combineRowsBy(
        pointRows,
        row => `${row.loadCase}-${round(row.pos, 2)}`,
        (a, b) => ({
          ...a,
          loadGuids: [...a.loadGuids, ...b.loadGuids],
          origin: mergeOrigins(a.origin, b.origin),
          force: a.force + b.force,
          friction: a.friction + b.friction,
        }),
      )
    : pointRows

  const lineColumns: GridColDef[] = [
    sourceColumn,
    textColumn('loadCase', 'Lastfall'),
    categoryColumn,
    numberColumn('start', 'Start', 'Start', 'm'),
    numberColumn('end', 'Ende', 'Ende', 'm'),
    numberColumn(
      'q_za',
      <span>
        q<sub>z,a</sub>
      </span>,
      'qz,a',
      'kN/m',
    ),
    numberColumn(
      'q_ze',
      <span>
        q<sub>z,e</sub>
      </span>,
      'qz,e',
      'kN/m',
    ),
    frictionColumn,
  ]
  const lineRows = loads.vertical_line_loads.map((load, index) => ({
    id: index,
    loadGuids: [shearAnchorLoadGuid(load.load_guid, elementGuid, 'vertical-line', index)],
    origin: load.origin_label && load.origin_label !== '-' ? load.origin_label : 'extern',
    loadCase: formatLoadCase(load.load_case, load.load_case_type),
    category: formatLoadCategoryShort(load.load_case),
    start: load.pos_start,
    end: load.pos_end,
    q_za: load.q_z_start / 1000,
    q_ze: load.q_z_end / 1000,
    friction: load.friction_shear_flow / 1000,
  }))
  const lineRowsDisplayed = loadsCombined
    ? combineRowsBy(
        lineRows,
        row => `${row.loadCase}-${round(row.start, 2)}-${round(row.end, 2)}`,
        (a, b) => ({
          ...a,
          loadGuids: [...a.loadGuids, ...b.loadGuids],
          origin: mergeOrigins(a.origin, b.origin),
          q_za: a.q_za + b.q_za,
          q_ze: a.q_ze + b.q_ze,
          friction: a.friction + b.friction,
        }),
      )
    : lineRows

  const tabPanelSx = { px: 0, pt: 1, pb: 0 } as const

  return (
    <Box sx={{ pb: 1 }}>
      <TabContext value={tab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: 12,
              paddingX: ({ spacing }) => spacing(1.5),
            },
          }}
        >
          <TabList scrollButtons="auto" variant="scrollable" onChange={(_, value) => setTab(value)}>
            <Tab value="horizontal" label="Horizontallasten" />
            <Tab value="point" label="Punkt-Auflasten" />
            <Tab value="line" label="Linien-Auflasten" />
          </TabList>
        </Box>
        <TabPanel value="horizontal" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[visibilityColumn(horizontalRowsDisplayed), ...horizontalColumns]}
            rows={horizontalRowsDisplayed}
            emptyMessage="Keine horizontalen Lasten"
          />
        </TabPanel>
        <TabPanel value="point" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[visibilityColumn(pointRowsDisplayed), ...pointColumns]}
            rows={pointRowsDisplayed}
            emptyMessage="Keine Punkt-Auflasten"
            description={verticalLoadDescription(frictionCoefficient)}
          />
        </TabPanel>
        <TabPanel value="line" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[visibilityColumn(lineRowsDisplayed), ...lineColumns]}
            rows={lineRowsDisplayed}
            emptyMessage="Keine Linien-Auflasten"
            description={verticalLoadDescription(frictionCoefficient)}
          />
        </TabPanel>
      </TabContext>
    </Box>
  )
}

export default ShearAnchorLoadResults
